import { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import { supabase } from '../lib/supabase';

export default function AnnouncementsTab({ levelCode, teacherId }) {
  const [announcements, setAnnouncements] = useState([]);
  const [showAnnouncementForm, setShowAnnouncementForm] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingField, setEditingField] = useState(null); // Track which field is being edited

  useEffect(() => {
    fetchAnnouncements();
  }, [levelCode]);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('updates')
        .select('*')
        .eq('level_code', levelCode)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAnnouncements(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAnnouncementSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      if (editingAnnouncement) {
        const { error } = await supabase
          .from('updates')
          .update({
            title: title,
            content: content,
          })
          .eq('id', editingAnnouncement.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('updates')
          .insert([{
            title: title,
            content: content,
            level_code: levelCode,
            teacher_id: teacherId,
            created_at: new Date().toISOString()
          }]);

        if (error) throw error;
      }

      setTitle('');
      setContent('');
      setShowAnnouncementForm(false);
      setEditingAnnouncement(null);
      fetchAnnouncements();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    if (window.confirm('Are you sure you want to delete this announcement?')) {
      try {
        const { error } = await supabase
          .from('updates')
          .delete()
          .eq('id', id);

        if (error) throw error;
        fetchAnnouncements();
      } catch (err) {
        setError(err.message);
      }
    }
  };

  const startEditingAnnouncement = (announcement) => {
    setEditingAnnouncement(announcement);
    setTitle(announcement.title);
    setContent(announcement.content);
    setShowAnnouncementForm(true);
  };

  const handleInlineEdit = async (announcementId, field, value) => {
    try {
      const { error } = await supabase
        .from('updates')
        .update({ [field]: value })
        .eq('id', announcementId);

      if (error) throw error;
      
      // Update local state
      setAnnouncements(announcements.map(announcement => 
        announcement.id === announcementId 
          ? { ...announcement, [field]: value }
          : announcement
      ));
    } catch (err) {
      setError(err.message);
    }
  };

  const startInlineEdit = (announcementId, field) => {
    setEditingField(`${announcementId}-${field}`);
  };

  const cancelInlineEdit = () => {
    setEditingField(null);
  };

  const saveInlineEdit = async (announcementId, field, value) => {
    if (value && value.trim() !== '') {
      await handleInlineEdit(announcementId, field, value.trim());
    }
    setEditingField(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 loading-spinner"></div>
          <p className="text-neutral-600">Loading announcements...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-neutral-900">Updates</h2>
          <p className="text-sm text-neutral-600">Share updates and important information with your class</p>
        </div>
        
        <button
          onClick={() => {
            setEditingAnnouncement(null);
            setTitle('');
            setContent('');
            setShowAnnouncementForm(true);
          }}
          className="btn-primary px-3 py-2 text-sm font-medium whitespace-nowrap"
        >
          <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          New Update
        </button>
      </div>

      {error && (
        <div className="card p-3 border-l-4 border-error-500 bg-error-50">
          <div className="flex items-center">
            <svg className="w-4 h-4 text-error-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-error-700 font-medium text-sm">{error}</p>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {announcements.length === 0 ? (
          <div className="card p-6 text-center">
            <div className="w-12 h-12 bg-neutral-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            <h3 className="text-base font-semibold text-neutral-900 mb-1">No updates yet</h3>
            <p className="text-sm text-neutral-500">Create your first update to share with your class.</p>
          </div>
        ) : (
          announcements.map((announcement, index) => (
            <div key={announcement.id} className="card p-4">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-3">
                <div className="flex-1 min-w-0">
                  {editingField === `${announcement.id}-title` ? (
                    <input
                      type="text"
                      defaultValue={announcement.title}
                      className="w-full text-base font-bold text-neutral-900 bg-transparent border-b border-primary-500 focus:outline-none focus:border-primary-600"
                      onBlur={(e) => saveInlineEdit(announcement.id, 'title', e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.target.blur();
                        } else if (e.key === 'Escape') {
                          cancelInlineEdit();
                        }
                      }}
                      autoFocus
                    />
                  ) : (
                    <h3 
                      className="text-base font-bold text-neutral-900 cursor-pointer hover:bg-neutral-50 p-2 -ml-2 rounded-lg transition-colors duration-200"
                      onClick={() => startInlineEdit(announcement.id, 'title')}
                    >
                      {announcement.title}
                    </h3>
                  )}
                  <p className="text-xs text-neutral-500 mt-1">
                    {new Date(announcement.created_at).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric', 
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
                
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => startEditingAnnouncement(announcement)}
                    className="btn-secondary text-xs px-2 py-1.5"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteAnnouncement(announcement.id)}
                    className="btn-ghost text-error-600 hover:text-error-700 text-xs px-2 py-1.5"
                  >
                    Delete
                  </button>
                </div>
              </div>
              
              <div className="prose prose-sm max-w-none">
                {editingField === `${announcement.id}-content` ? (
                  <textarea
                    defaultValue={announcement.content}
                    className="w-full p-2 text-sm border border-primary-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                    rows={4}
                    onBlur={(e) => saveInlineEdit(announcement.id, 'content', e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Escape') {
                        cancelInlineEdit();
                      }
                    }}
                    autoFocus
                  />
                ) : (
                  <div 
                    className="cursor-pointer hover:bg-neutral-50 p-2 -mx-2 rounded-lg transition-colors duration-200 text-sm"
                    onClick={() => startInlineEdit(announcement.id, 'content')}
                  >
                    <ReactMarkdown rehypePlugins={[rehypeSanitize]}>
                      {announcement.content}
                    </ReactMarkdown>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Announcement Form Modal */}
      {showAnnouncementForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center sm:justify-center p-0 sm:p-4 z-50 safe-top safe-bottom">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-large w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto animate-scale-in">
            <div className="sticky top-0 bg-white border-b border-neutral-200 p-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-neutral-900">
                {editingAnnouncement ? 'Edit Update' : 'New Update'}
              </h3>
              <button
                onClick={() => {
                  setShowAnnouncementForm(false);
                  setEditingAnnouncement(null);
                  setTitle('');
                  setContent('');
                }}
                className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-4 sm:p-6">
              {error && (
                <div className="mb-4 p-3 bg-error-50 border border-error-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <svg className="h-4 w-4 text-error-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-error-700 font-medium text-sm">{error}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleAnnouncementSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">Title</label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="input text-sm"
                    placeholder="Enter the title"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">Content</label>
                  <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    className="input text-sm"
                    rows={6}
                    placeholder="What's new in class today?"
                    required
                  />
                </div>

                <div className="flex flex-col sm:flex-row justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAnnouncementForm(false);
                      setEditingAnnouncement(null);
                      setTitle('');
                      setContent('');
                    }}
                    className="btn-secondary text-sm py-2"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary text-sm py-2"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-4 h-4 loading-spinner mr-2"></div>
                        {editingAnnouncement ? 'Updating...' : 'Posting...'}
                      </>
                    ) : (
                      editingAnnouncement ? 'Update' : 'Post Update'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
